import { FileText } from 'lucide-react';
import React, { memo, ReactNode, useState } from 'react';

import { clsx } from 'clsx';
import Image from 'next/image';

import { receiptOrder } from '@screens/OrderHistoryDetail/api/receiptOrder/receiptOrder';
import BaikalLogo from '@shared/assets/icons/baikal.svg';
import CdekLogo from '@shared/assets/icons/cdek.svg';
import CreditCard from '@shared/assets/icons/credit-card.svg';
import SplitLogo from '@shared/assets/icons/split.svg';
import YaPayLogo from '@shared/assets/icons/yapay.svg';
import { DeliveryCompany } from '@shared/types/enums/delivery.enum';
import { PaymentMethodType } from '@shared/types/enums/pay.enum';
import { Button } from '@shared/ui/Button';
import { Divider } from '@shared/ui/Divider/Divider';
import { HStack, VStack } from '@shared/ui/Stack';
import { getInflectedWord } from '@shared/utils/getInflectedWord/getInflectedWord';

import cls from './PaymentPlate.module.scss';

type PaymentPlateType = {
  quantity: number;
  totalAmount: number;
  buttonLabel: string;
  onClickButton: () => void;
  date?: string;
  deliveryCompany?: DeliveryCompany;
  paymentMethod?: PaymentMethodType;
  buttonIcon?: ReactNode;
  loading?: boolean;
  outStock?: boolean;
  orderNumber?: string;
  children?: ReactNode;
};

export const PaymentPlate = memo<PaymentPlateType>(
  ({
    loading = false,
    buttonLabel,
    quantity,
    outStock,
    totalAmount,
    onClickButton,
    orderNumber,
    buttonIcon,
    date,
    deliveryCompany,
    paymentMethod,
    children
  }) => {
    const [loadingReceipt, setLoadingReceipt] = useState(false);

    const isReady = quantity > 0 && !outStock;

    const onClickReceiptLink = async () => {
      if (orderNumber) {
        setLoadingReceipt(true);

        const { data } = await receiptOrder(orderNumber);
        if (data?.url) {
          window.open(data.url, '_blank');
        }

        setLoadingReceipt(false);
      }
    };

    return (
      <VStack className={cls.mainWrapper}>
        <VStack className={cls.wrapper}>
          <VStack className={cls.plate}>
            <HStack className={cls.textWrapper}>
              <div className={cls.text}>Ваш заказ</div>
              <div
                className={cls.quantity}
              >{`${quantity} ${getInflectedWord(quantity, ['товар', 'товара', 'товаров'])}`}</div>
            </HStack>
            {(date || paymentMethod) && (
              <div className={cls.detailsWrapper}>
                {deliveryCompany && (
                  <HStack className={cls.row}>
                    <div>Служба доставки</div>
                    {deliveryCompany === DeliveryCompany.CDEK ? (
                      <Image
                        src={CdekLogo}
                        width={51}
                        alt={''}
                      />
                    ) : (
                      <Image
                        src={BaikalLogo}
                        width={108}
                        alt={''}
                      />
                    )}
                  </HStack>
                )}
                {date && (
                  <HStack className={cls.row}>
                    <div>Дата заказа</div>
                    <div>{date}</div>
                  </HStack>
                )}
                {paymentMethod && (
                  <HStack className={cls.row}>
                    <div>Оплата</div>
                    <div>
                      {(paymentMethod === PaymentMethodType.BANK_CARD && (
                        <HStack className={clsx(cls.row, cls.bankMethodWrapper)}>
                          <Image
                            alt={''}
                            src={CreditCard}
                          />
                          <div>Карта </div>
                        </HStack>
                      )) ||
                        (paymentMethod === PaymentMethodType.YANDEX_PAY && (
                          <Image
                            alt={''}
                            src={YaPayLogo}
                          />
                        )) || (
                          <Image
                            alt={''}
                            src={SplitLogo}
                          />
                        )}
                    </div>
                  </HStack>
                )}
              </div>
            )}
            <Divider />
            <HStack className={cls.textWrapper}>
              <div className={cls.text}>Итого</div>
              <div className={cls.text}>{totalAmount}&nbsp;₽</div>
            </HStack>
            {outStock && <div className={cls.alert}>Часть товаров нет в наличии</div>}
          </VStack>
          <Button
            disabled={!isReady}
            size={'l'}
            fullWidth
            loadingButton
            addonLeft={buttonIcon}
            isLoading={loading}
            onClick={onClickButton}
          >
            {buttonLabel}
          </Button>
          {orderNumber && (
            <Button
              fullWidth
              size={'l'}
              variant={'secondary'}
              loadingButton
              addonLeft={<FileText width={16} />}
              isLoading={loadingReceipt}
              onClick={onClickReceiptLink}
            >
              Ссылка на чек
            </Button>
          )}
        </VStack>
        {children}
      </VStack>
    );
  }
);
